import { useState, useCallback, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";

interface DashboardStats {
  totalOrders: number;
  pendingOrders: number;
  completedOrders: number;
  totalRevenue: number;
  totalProducts: number;
  lowStockProducts: number;
  totalCustomers: number;
  pendingCreditApps: number;
}

export function useDashboardStats(webshopIds: number[]) {
  const [stats, setStats] = useState<DashboardStats>({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalRevenue: 0,
    totalProducts: 0,
    lowStockProducts: 0,
    totalCustomers: 0,
    pendingCreditApps: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchStats = useCallback(async () => {
    if (!webshopIds || webshopIds.length === 0) {
      setLoading(false);
      setStats({
        totalOrders: 0,
        pendingOrders: 0,
        completedOrders: 0,
        totalRevenue: 0,
        totalProducts: 0,
        lowStockProducts: 0,
        totalCustomers: 0,
        pendingCreditApps: 0,
      });
      return;
    }

    try {
      setLoading(true);

      // Fetch all data in parallel for maximum performance
      const [ordersResult, productsResult, customersResult, creditAppsResult] = await Promise.all([
        supabase
          .from('orders')
          .select('order_status, captured_amount')
          .in('webshop_id', webshopIds),
        
        supabase
          .from('articles')
          .select('stock_count, webstock_limit')
          .in('webshop_id', webshopIds),
        
        supabase
          .from('customers')
          .select('id')
          .in('webshop_id', webshopIds),
        
        supabase
          .from('customers')
          .select('id')
          .in('webshop_id', webshopIds)
          .eq('credit_approved', false)
      ]);

      if (ordersResult.error) throw ordersResult.error;
      if (productsResult.error) throw productsResult.error;
      if (customersResult.error) throw customersResult.error;
      if (creditAppsResult.error) throw creditAppsResult.error;

      const orders = ordersResult.data || [];
      const products = productsResult.data || [];
      
      setStats({
        totalOrders: orders.length,
        pendingOrders: orders.filter(o => o.order_status === 'pending').length,
        completedOrders: orders.filter(o => o.order_status === 'delivered').length,
        totalRevenue: orders.reduce((sum, o) => sum + (Number(o.captured_amount) || 0), 0),
        totalProducts: products.length,
        lowStockProducts: products.filter(p => {
          const stock = p.stock_count || 0;
          const limit = p.webstock_limit || 10;
          return stock < limit;
        }).length,
        totalCustomers: customersResult.data?.length || 0,
        pendingCreditApps: creditAppsResult.data?.length || 0,
      });
      
      setError(null);
    } catch (err) {
      console.error('Dashboard stats error:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [webshopIds]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
}
